import React from "react";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { ScrollArea } from "@/components/ui/scroll-area";
import logoIcon from "@/assets/regard-de-nature-logo.png";
import { Shield } from "lucide-react";
import { BRAND } from "@/config/brand";

interface PrivacyPolicyProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  triggerClassName?: string;
  asChild?: boolean;
  children?: React.ReactNode;
}

export const PrivacyPolicy = ({
  open,
  onOpenChange,
  triggerClassName,
  asChild = true,
  children,
}: PrivacyPolicyProps) => {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetTrigger asChild={asChild}>
        {children || (
          <button className={triggerClassName || "hover:text-primary transition-colors text-left"}>
            Politique de confidentialité
          </button>
        )}
      </SheetTrigger>
      <SheetContent side="right" className="w-full sm:max-w-2xl">
        <SheetHeader className="space-y-4 pb-6 border-b">
          <div className="flex flex-col items-center gap-3">
            <img src={logoIcon} alt={BRAND.name.full} className="w-16 h-16" />
            <div className="text-center">
              <SheetTitle className="text-3xl font-cinzel text-primary">{BRAND.name.full}</SheetTitle>
              <p className="text-sm font-montserrat font-semibold text-secondary uppercase tracking-wider mt-1">
                {BRAND.baseline.full}
              </p>
            </div>
          </div>
          <SheetDescription className="text-center text-lg font-medium text-foreground flex items-center justify-center gap-2">
            <Shield className="w-5 h-5 text-primary" />
            Politique de Confidentialité
          </SheetDescription>
        </SheetHeader>

        <ScrollArea className="h-[calc(100vh-260px)] mt-6 pr-4 pb-12">
          <div className="space-y-6 text-sm">
            <section>
              <h3 className="text-lg font-semibold text-primary mb-3">1. Introduction</h3>
              <p className="text-muted-foreground leading-relaxed">
                La présente politique de confidentialité a pour but d'informer les utilisateurs du site sur la manière
                dont leurs données personnelles sont collectées, traitées et protégées par {BRAND.name.full} -{" "}
                {BRAND.baseline.full} Praticienne en Santé Naturelle.
              </p>
              <p className="text-muted-foreground leading-relaxed mt-2">
                {BRAND.name.full} s'engage à respecter la vie privée de ses clients et à protéger leurs données
                personnelles conformément au Règlement Général sur la Protection des Données (RGPD) et à la loi
                Informatique et Libertés.
              </p>
            </section>

            <section>
              <h3 className="text-lg font-semibold text-primary mb-3">2. Responsable du traitement</h3>
              <div className="space-y-1 text-muted-foreground">
                <p className="font-medium text-foreground">
                  {BRAND.name.full} – {BRAND.baseline.full}
                </p>
                <p>Adresse : 31 rue de l'ancien petit fossé, 31450 Montesquieu-Lauragais</p>
                <p>SIRET : 111 111 111 00000</p>
                <p>Email : contact@regard-de-nature.fr</p>
                <p>Téléphone : 06 37 03 18 66</p>
              </div>
            </section>

            <section>
              <h3 className="text-lg font-semibold text-primary mb-3">3. Données collectées</h3>
              <p className="text-muted-foreground leading-relaxed mb-3">
                Dans le cadre de l'utilisation du site et de la prise de rendez-vous, nous sommes amenés à collecter les
                données personnelles suivantes :
              </p>
              <ul className="list-disc list-inside space-y-1 text-muted-foreground ml-2">
                <li>Nom et prénom</li>
                <li>Date de naissance</li>
                <li>Adresse email</li>
                <li>Numéro de téléphone</li>
                <li>Adresse postale (si nécessaire)</li>
                <li>Informations relatives à votre état de santé (uniquement dans le cadre des séances)</li>
                <li>Historique des rendez-vous et des prestations</li>
              </ul>
              <p className="text-muted-foreground leading-relaxed mt-3">
                Ces données sont collectées directement auprès de vous lors de votre prise de contact, de votre prise de
                rendez-vous ou lors des séances.
              </p>
            </section>

            <section>
              <h3 className="text-lg font-semibold text-primary mb-3">4. Finalités du traitement</h3>
              <p className="text-muted-foreground leading-relaxed mb-3">
                Les données personnelles collectées sont utilisées aux fins suivantes :
              </p>
              <ul className="list-disc list-inside space-y-1 text-muted-foreground ml-2">
                <li>Gestion des prises de rendez-vous et des consultations</li>
                <li>Suivi des séances et accompagnement personnalisé</li>
                <li>Communication avec les clients (confirmations, rappels, informations)</li>
                <li>Gestion administrative et comptable</li>
                <li>Respect des obligations légales et réglementaires</li>
                <li>Amélioration de la qualité des services proposés</li>
                <li>Envoi d'informations sur les événements et actualités (avec votre consentement)</li>
              </ul>
            </section>

            <section>
              <h3 className="text-lg font-semibold text-primary mb-3">5. Base légale du traitement</h3>
              <p className="text-muted-foreground leading-relaxed mb-3">
                Le traitement de vos données personnelles repose sur les bases légales suivantes :
              </p>
              <ul className="list-disc list-inside space-y-1 text-muted-foreground ml-2">
                <li>L'exécution d'un contrat : gestion des rendez-vous et prestations</li>
                <li>Le respect d'obligations légales : obligations comptables et fiscales</li>
                <li>Le consentement : pour l'envoi de communications marketing</li>
                <li>L'intérêt légitime : amélioration des services</li>
              </ul>
            </section>

            <section>
              <h3 className="text-lg font-semibold text-primary mb-3">6. Destinataires des données</h3>
              <p className="text-muted-foreground leading-relaxed mb-3">
                Vos données personnelles sont destinées exclusivement à :
              </p>
              <ul className="list-disc list-inside space-y-1 text-muted-foreground ml-2">
                <li>
                  {BRAND.name.full} – {BRAND.baseline.full}, en sa qualité de praticienne
                </li>
                <li>Le prestataires technique assurant l'hébergement et la maintenance du site</li>
                <li>Les autorités légales sur demande justifiée</li>
              </ul>
              <p className="text-muted-foreground leading-relaxed mt-3">
                Vos données ne sont en aucun cas vendues, louées ou cédées à des tiers à des fins commerciales.
              </p>
            </section>

            <section>
              <h3 className="text-lg font-semibold text-primary mb-3">7. Durée de conservation</h3>
              <p className="text-muted-foreground leading-relaxed mb-3">
                Les données personnelles sont conservées pour les durées suivantes :
              </p>
              <ul className="list-disc list-inside space-y-1 text-muted-foreground ml-2">
                <li>
                  Données de contact et de rendez-vous : pendant la durée de la relation client et 3 ans après le
                  dernier contact
                </li>
                <li>Données de santé : 10 ans après la dernière séance (conformément aux obligations légales)</li>
                <li>Données comptables : 10 ans conformément aux obligations légales</li>
                <li>Données de prospection : 3 ans à compter du dernier contact</li>
              </ul>
              <p className="text-muted-foreground leading-relaxed mt-3">
                Au-delà de ces durées, vos données sont supprimées de manière sécurisée.
              </p>
            </section>

            <section>
              <h3 className="text-lg font-semibold text-primary mb-3">8. Sécurité des données</h3>
              <p className="text-muted-foreground leading-relaxed mb-3">
                {BRAND.name.full} – {BRAND.baseline.full} met en œuvre toutes les mesures techniques et
                organisationnelles appropriées pour protéger vos données personnelles contre la destruction, la perte,
                l'altération, la divulgation non autorisée ou l'accès non autorisé.
              </p>
              <p className="text-muted-foreground leading-relaxed mb-3">Ces mesures incluent notamment :</p>
              <ul className="list-disc list-inside space-y-1 text-muted-foreground ml-2">
                <li>Hébergement sécurisé du site</li>
                <li>Accès restreint aux données personnelles</li>
                <li>Sauvegardes régulières</li>
                <li>Formation à la protection des données</li>
              </ul>
            </section>

            <section>
              <h3 className="text-lg font-semibold text-primary mb-3">9. Vos droits</h3>
              <p className="text-muted-foreground leading-relaxed mb-3">
                Conformément au RGPD, vous disposez des droits suivants concernant vos données personnelles :
              </p>
              <ul className="list-disc list-inside space-y-1 text-muted-foreground ml-2">
                <li>
                  <span className="font-medium text-foreground">Droit d'accès :</span> obtenir la confirmation que des
                  données vous concernant sont traitées et en obtenir une copie
                </li>
                <li>
                  <span className="font-medium text-foreground">Droit de rectification :</span> demander la correction
                  de données inexactes ou incomplètes
                </li>
                <li>
                  <span className="font-medium text-foreground">Droit à l'effacement :</span> demander la suppression de
                  vos données dans certaines conditions
                </li>
                <li>
                  <span className="font-medium text-foreground">Droit à la limitation du traitement :</span> demander la
                  limitation du traitement de vos données
                </li>
                <li>
                  <span className="font-medium text-foreground">Droit à la portabilité :</span> recevoir vos données
                  dans un format structuré et couramment utilisé
                </li>
                <li>
                  <span className="font-medium text-foreground">Droit d'opposition :</span> vous opposer au traitement
                  de vos données pour des raisons tenant à votre situation particulière
                </li>
                <li>
                  <span className="font-medium text-foreground">Droit de retirer votre consentement :</span> pour les
                  traitements basés sur le consentement
                </li>
              </ul>
              <p className="text-muted-foreground leading-relaxed mt-3 mb-3">
                Pour exercer ces droits, vous pouvez nous contacter :
              </p>
              <ul className="list-disc list-inside space-y-1 text-muted-foreground ml-2">
                <li>Par email : contact@regard-de-nature.fr</li>
                <li>Par téléphone : 06 37 03 18 66</li>
                <li>
                  Par courrier : {BRAND.name.full} – {BRAND.baseline.full}, 31 rue de l'ancien petit fossé, 31450
                  Montesquieu-Lauragais
                </li>
              </ul>
              <p className="text-muted-foreground leading-relaxed mt-3">
                Vous devrez justifier de votre identité lors de votre demande. Nous nous engageons à répondre dans un
                délai maximum d'un mois.
              </p>
            </section>

            <section>
              <h3 className="text-lg font-semibold text-primary mb-3">10. Cookies</h3>
              <p className="text-muted-foreground leading-relaxed">
                Le site peut utiliser des cookies pour améliorer l'expérience utilisateur et analyser la fréquentation
                du site.
              </p>
              <p className="text-muted-foreground leading-relaxed mt-2">
                Vous pouvez paramétrer votre navigateur pour refuser les cookies ou être informé de leur dépôt.
                Cependant, certaines fonctionnalités du site peuvent être affectées.
              </p>
            </section>

            <section>
              <h3 className="text-lg font-semibold text-primary mb-3">11. Droit de réclamation</h3>
              <p className="text-muted-foreground leading-relaxed mb-3">
                Si vous estimez que vos droits ne sont pas respectés, vous avez le droit d'introduire une réclamation
                auprès de la Commission Nationale de l'Informatique et des Libertés (CNIL) :
              </p>
              <div className="space-y-1 text-muted-foreground">
                <p className="font-medium text-foreground">CNIL</p>
                <p>3 Place de Fontenoy</p>
                <p>TSA 80715</p>
                <p>75334 PARIS CEDEX 07</p>
                <p>Téléphone : 01 53 73 22 22</p>
                <p>Site web : www.cnil.fr</p>
              </div>
            </section>

            <section>
              <h3 className="text-lg font-semibold text-primary mb-3">
                12. Modifications de la politique de confidentialité
              </h3>
              <p className="text-muted-foreground leading-relaxed">
                La présente politique de confidentialité peut être modifiée à tout moment pour s'adapter aux évolutions
                législatives ou aux changements de nos pratiques.
              </p>
              <p className="text-muted-foreground leading-relaxed mt-2">
                Toute modification sera publiée sur cette page avec indication de la date de dernière mise à jour.
              </p>
              <p className="text-muted-foreground leading-relaxed mt-2 font-medium">
                Dernière mise à jour : Janvier 2026
              </p>
            </section>

            <section className="border-t border-border pt-6">
              <h3 className="text-lg font-semibold text-primary mb-3">Contact</h3>
              <p className="text-muted-foreground leading-relaxed mb-3">
                Pour toute question concernant la protection de vos données personnelles :
              </p>
              <div className="space-y-1 text-muted-foreground">
                <p className="font-medium text-foreground">
                  {BRAND.name.full} – {BRAND.baseline.full}
                </p>
                <p>31 rue de l'ancien petit fossé, 31450 Montesquieu-Lauragais</p>
                <p>Téléphone : 06 37 03 18 66</p>
                <p>Email : contact@regard-de-nature.fr</p>
              </div>
            </section>
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
};
